/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { ExecuteActionPlanResponseData } from "@shared/types/responses/learning";

export interface ActionPlanResults {
  // Created entities with their details
  topics: Array<{ id: number; name: string; category?: string; description?: string }>;
  skills: Array<{ id: number; name: string; level?: number; description?: string }>;
  learningActivities: Array<{ id: number; title: string; activityType?: string; description?: string; duration?: number }>;
  learningPlans: Array<{ id: number; title: string; description?: string }>;
  learningModules: Array<{ id: number; title: string; description?: string }>;
  learningPaths: Array<{ id: number; title: string; description?: string }>;
  learningOutcomes: Array<{ id: number; title: string; skillId?: number; status?: string }>;
  
  // Graph entities
  topicGraphs: Array<{ id: number; title: string; nodeCount?: number; relationCount?: number }>;
  skillGraphs: Array<{ id: number; title: string; nodeCount?: number; relationCount?: number }>;
  
  // Summary counts for IDs-only entities
  topicIds: number[];
  skillIds: number[];
  
  // Plan execution metadata
  planInfo?: {
    planId: string;
    userRequest: string;
    totalSteps: number;
    completedSteps: number;
    failedSteps: number;
    executionTime: number;
  };
  
  // Step details for debugging/transparency
  stepSummary?: Array<{
    stepId: string;
    action: string;
    target: string;
    success: boolean;
    description?: string;
    error?: string;
  }>;
}

/**
 * Extract comprehensive results from action plan execution data
 * 
 * Processes ExecuteActionPlanResponseData to extract all created learning entities
 * including topics, skills, learning activities, plans, modules, paths, outcomes,
 * and knowledge graphs, along with execution metadata and step summaries.
 * 
 * @param executionResult - The response data from action plan execution
 * @returns ActionPlanResults with organized entity data and metadata
 */
export function extractActionPlanResults(executionResult: ExecuteActionPlanResponseData): ActionPlanResults {
  const results: ActionPlanResults = {
    topics: [],
    skills: [],
    learningActivities: [],
    learningPlans: [],
    learningModules: [],
    learningPaths: [],
    learningOutcomes: [],
    topicGraphs: [],
    skillGraphs: [],
    topicIds: [],
    skillIds: [],
    stepSummary: []
  };

  // Debug logging to understand execution result structure
  console.log('Extracting action plan results:', {
    hasExecutionResult: !!executionResult,
    stepResultsCount: executionResult?.stepResults?.length || 0,
    stepResults: executionResult?.stepResults?.map(sr => ({
      stepId: sr.stepId,
      success: sr.result?.success || false,
      actionType: sr.result?.action?.actionType,
      target: sr.result?.action?.target,
      resultKeys: sr.result?.result ? Object.keys(sr.result.result) : []
    })) || []
  });

  // Add plan metadata
  if (executionResult) {
    results.planInfo = {
      planId: executionResult.planId,
      userRequest: executionResult.result?.userRequest || 'Learning content generation',
      totalSteps: executionResult.summary.totalSteps,
      completedSteps: executionResult.summary.completedSteps,
      failedSteps: executionResult.summary.failedSteps,
      executionTime: executionResult.summary.executionTimeMs
    };
  }

  // Process each step result
  executionResult?.stepResults.forEach(stepResult => {
    // Add step summary for transparency
    results.stepSummary?.push({
      stepId: stepResult.stepId,
      action: stepResult.result?.action?.actionType || 'unknown',
      target: stepResult.result?.action?.target || 'unknown',
      success: stepResult.result?.success || false,  // Fixed: check nested success
      description: stepResult.result?.action?.description,
      error: stepResult.result?.error
    });

    if (stepResult.result?.success && stepResult.result) {  // Fixed: check nested success
      // The actual step execution result is nested under stepResult.result.result
      // stepResult.result contains: { success, action, result, error, executedAt, metadata }
      const result = stepResult.result.result || stepResult.result;
      
      // Extract topics from simple step results
      if (result.topicId) {
        results.topics.push({
          id: result.topicId,
          name: result.name || `Topic ${result.topicId}`,
          category: result.category,
          description: result.description
        });
        results.topicIds.push(result.topicId);
      }
      
      // Extract skills
      if (result.skillId) {
        results.skills.push({
          id: result.skillId,
          name: result.name || `Skill ${result.skillId}`,
          level: result.level,
          description: result.description
        });
        results.skillIds.push(result.skillId);
      }
      
      // Extract learning activities
      if (result.activityId) {
        results.learningActivities.push({
          id: result.activityId,
          title: result.title || `Activity ${result.activityId}`,
          activityType: result.activityType,
          description: result.description,
          duration: result.duration
        });
      }
      
      // Extract learning plans
      if (result.planId && typeof result.planId === 'number') {
        results.learningPlans.push({
          id: result.planId,
          title: result.title || `Plan ${result.planId}`,
          description: result.description
        });
      }
      
      // Extract learning modules
      if (result.moduleId) {
        results.learningModules.push({
          id: result.moduleId,
          title: result.title || `Module ${result.moduleId}`,
          description: result.description
        });
      }
      
      // Extract learning paths
      if (result.pathId && result.title) {
        results.learningPaths.push({
          id: result.pathId,
          title: result.title,
          description: result.description
        });
      }
      
      // Extract learning outcomes
      if (result.outcomeId) {
        results.learningOutcomes.push({
          id: result.outcomeId,
          title: result.title || `Outcome ${result.outcomeId}`,
          skillId: result.skillId,
          status: result.status
        });
      }
      
      // Extract topic graphs from simple step results
      if (result.graphId) {
        results.topicGraphs.push({
          id: result.graphId,
          title: result.title || `Topic Graph ${result.graphId}`,
          nodeCount: result.nodeCount,
          relationCount: result.relationCount
        });
      }
      
      // Extract skill graphs
      if (result.graphId && result.action?.target === 'skill_graph') {
        results.skillGraphs.push({
          id: result.graphId,
          title: result.title || `Skill Graph ${result.graphId}`,
          nodeCount: result.nodeCount,
          relationCount: result.relationCount
        });
      }

      // Handle tool execution results from complex steps
      if (result.toolExecution?.result) {
        const toolResult = result.toolExecution.result;
        
        console.log('Processing tool execution result:', {
          stepId: stepResult.stepId,
          toolName: result.toolExecution.toolName,
          toolResult,
          toolResultKeys: Object.keys(toolResult)
        });
        
        // Extract entities from tool results based on tool name or result structure
        if (toolResult.topicId || toolResult.topic) {
          const topic = toolResult.topic || { id: toolResult.topicId };
          results.topics.push({
            id: topic.id,
            name: topic.name || topic.title || `Topic ${topic.id}`,
            category: topic.category,
            description: topic.description
          });
          results.topicIds.push(topic.id);
        }
        
        if (toolResult.skillId || toolResult.skill) {
          const skill = toolResult.skill || { id: toolResult.skillId };
          results.skills.push({
            id: skill.id,
            name: skill.name || skill.title || `Skill ${skill.id}`,
            level: skill.level,
            description: skill.description
          });
          results.skillIds.push(skill.id);
        }
        
        if (toolResult.activityId || toolResult.learningActivity) {
          const activity = toolResult.learningActivity || { id: toolResult.activityId };
          results.learningActivities.push({
            id: activity.id,
            title: activity.title || `Activity ${activity.id}`,
            activityType: activity.activityType,
            description: activity.description,
            duration: activity.duration
          });
        }
        
        if (toolResult.moduleId || toolResult.learningModule) {
          const module = toolResult.learningModule || { id: toolResult.moduleId };
          results.learningModules.push({
            id: module.id,
            title: module.title || `Module ${module.id}`,
            description: module.description
          });
        }
        
        if (toolResult.pathId || toolResult.learningPath) {
          const path = toolResult.learningPath || { id: toolResult.pathId };
          results.learningPaths.push({
            id: path.id,
            title: path.title || `Path ${path.id}`,
            description: path.description
          });
        }
        
        if (toolResult.outcomeId || toolResult.learningOutcome) {
          const outcome = toolResult.learningOutcome || { id: toolResult.outcomeId };
          results.learningOutcomes.push({
            id: outcome.id,
            title: outcome.title || `Outcome ${outcome.id}`,
            skillId: outcome.skillId,
            status: outcome.status
          });
        }
        
        // Handle arrays of created entities
        if (Array.isArray(toolResult.created)) {
          toolResult.created.forEach((item: any) => {
            if (item && typeof item === 'object' && 'id' in item) {
              // Determine entity type and add to appropriate array
              if (item.skillName || item.level !== undefined) {
                results.skills.push({
                  id: item.id,
                  name: item.skillName || item.name || `Skill ${item.id}`,
                  level: item.level,
                  description: item.description
                });
                results.skillIds.push(item.id);
              } else if (item.topicName || item.category) {
                results.topics.push({
                  id: item.id,
                  name: item.topicName || item.name || `Topic ${item.id}`,
                  category: item.category,
                  description: item.description
                });
                results.topicIds.push(item.id);
              }
            }
          });
        }
      }

      // Handle legacy result structure (topicIds, skillIds arrays)
      if (result.topicIds && Array.isArray(result.topicIds)) {
        results.topicIds.push(...result.topicIds);
      }
      if (result.skillIds && Array.isArray(result.skillIds)) {
        results.skillIds.push(...result.skillIds);
      }
    }
  });

  // Remove duplicates
  results.topicIds = [...new Set(results.topicIds)];
  results.skillIds = [...new Set(results.skillIds)];

  // Debug logging to see what was extracted
  console.log('Extraction results:', {
    topicsCount: results.topics.length,
    topicGraphsCount: results.topicGraphs.length,
    skillsCount: results.skills.length,
    topics: results.topics,
    topicGraphs: results.topicGraphs,
    topicIds: results.topicIds,
    skillIds: results.skillIds
  });

  return results;
} 